const DEV_JWT_SECRET =
  (import.meta.env.VITE_DEV_JWT_SECRET as string | undefined) ??
  (import.meta.env.VITE_JWT_SECRET as string | undefined) ??
  "your-secret-key-change-this-in-production-make-it-at-least-256-bits";

const JWT_AUDIENCE =
  (import.meta.env.VITE_JWT_AUDIENCE as string | undefined) ?? "sami-cms";

const USE_UNSIGNED_JWT =
  (import.meta.env.VITE_JWT_USE_UNSIGNED as string | undefined) !== "false";

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = "";
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function base64UrlEncodeJson(value: unknown): string {
  const json = JSON.stringify(value);
  const bytes = new TextEncoder().encode(json);
  return base64UrlEncode(bytes);
}

async function signHmacSha256(data: string, secret: string): Promise<string> {
  const key = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(secret),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
  const signature = await crypto.subtle.sign(
    "HMAC",
    key,
    new TextEncoder().encode(data),
  );
  return base64UrlEncode(new Uint8Array(signature));
}

export interface CreateDevJwtInput {
  email: string;
}

/**
 * Session JWT for dev login (no tenant_id). Align VITE_JWT_SECRET with backend JWT_SECRET when signed.
 */
export async function createDevJwt({ email }: CreateDevJwtInput): Promise<string> {
  const now = Math.floor(Date.now() / 1000);
  const payload = {
    sub: email,
    email,
    name: email,
    preferred_username: email,
    roles: ["user"],
    permissions: [] as string[],
    groups: [] as string[],
    aud: JWT_AUDIENCE,
    iat: now,
    exp: now + 60 * 60 * 24,
    iss: "sami-dev-login",
  };

  if (USE_UNSIGNED_JWT) {
    const encodedHeader = base64UrlEncodeJson({ alg: "none", typ: "JWT" });
    const encodedPayload = base64UrlEncodeJson(payload);
    return `${encodedHeader}.${encodedPayload}.`;
  }

  const encodedHeader = base64UrlEncodeJson({ alg: "HS256", typ: "JWT" });
  const encodedPayload = base64UrlEncodeJson(payload);
  const signingInput = `${encodedHeader}.${encodedPayload}`;
  const signature = await signHmacSha256(signingInput, DEV_JWT_SECRET);
  return `${signingInput}.${signature}`;
}
